import { ArrowUpRight, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { FormSectionCard, FormSectionHeader } from '@/components/molecules';
import type { EstimateUpsell, UpsellRelationshipType } from '@/hooks/estimates/useEstimateUpsells';

// NOT-306 (spec §8) — the approved "Recommended add-ons" surface. Lists the
// non-auto upsell candidates (ordered by aiWeight from the endpoint); each can
// be added as a line item via the shared add-path.

const RELATIONSHIP_LABEL: Record<UpsellRelationshipType, string> = {
  upsell: 'Upsell',
  accessory: 'Accessory',
  alternative: 'Alternative',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

interface EstimateUpsellRecommendationsProps {
  recommendations: EstimateUpsell[];
  onAdd: (upsell: EstimateUpsell) => void;
}

export function EstimateUpsellRecommendations({
  recommendations,
  onAdd,
}: EstimateUpsellRecommendationsProps) {
  if (recommendations.length === 0) return null;

  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <FormSectionHeader
        iconNode={
          <span className="mt-0.5 shrink-0 text-muted-foreground">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        }
        title={`Recommended add-ons (${recommendations.length})`}
        description="Based on the items and total on this estimate."
        className="mb-3"
      />

      <div className="space-y-2">
        {recommendations.map((rec) => (
          <div
            key={rec.suggestedItem.id}
            className="flex items-start justify-between gap-3 rounded-lg border bg-background p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-sm">{rec.suggestedItem.name}</p>
                <Badge
                  variant="outline"
                  className="border-warning/30 bg-warning/10 text-[10px] text-warning"
                >
                  {RELATIONSHIP_LABEL[rec.relationshipType]}
                </Badge>
              </div>
              {rec.reason ? (
                <p className="mt-0.5 text-xs text-muted-foreground">{rec.reason}</p>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <span className="text-sm font-medium">
                {formatCurrency(rec.suggestedItem.basePrice)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => onAdd(rec)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add
              </Button>
            </div>
          </div>
        ))}
      </div>
    </FormSectionCard>
  );
}
